// Chart rendering functions

import { formatNumber } from './utils';

// Chart instances (for cleanup)
let durationChart: any = null;
let messagesChart: any = null;
let durationTimelineChart: any = null;
let productRadarChart: any = null;
let productBubbleChart: any = null;
let trafficSourcesChart: any = null;
let deviceTypeChart: any = null;
let assistedComparisonChart: any = null;

const Chart = (window as any).Chart;

// Color palettes
const colors = {
  primary: ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a'],
  funnel: ['#3498db', '#9b59b6', '#e67e22', '#1abc9c', '#27ae60'],
  gradients: {
    purple: 'rgba(102, 126, 234, 0.2)',
    pink: 'rgba(240, 147, 251, 0.2)'
  }
};

export function renderDurationChart(distribution: Array<{ duration_range: string; session_count: number }>): void {
  const ctx = document.getElementById('durationChart') as HTMLCanvasElement;
  if (!ctx) return;

  if (durationChart) durationChart.destroy();

  const labels = distribution.map(d => d.duration_range);
  const data = distribution.map(d => parseInt(String(d.session_count)));

  durationChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels,
      datasets: [{
        data,
        backgroundColor: colors.primary,
        borderWidth: 3,
        borderColor: '#fff',
        hoverOffset: 15
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      cutout: '65%',
      plugins: {
        legend: {
          position: 'right',
          labels: {
            padding: 15,
            font: { size: 13, weight: '500' },
            usePointStyle: true,
            pointStyle: 'circle'
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          cornerRadius: 8
        }
      }
    }
  });
}

export function renderMessagesChart(distribution: Array<{ message_range: string; session_count: number }>): void {
  const ctx = document.getElementById('messagesChart') as HTMLCanvasElement;
  if (!ctx) return;

  if (messagesChart) messagesChart.destroy();

  const labels = distribution.map(d => d.message_range);
  const data = distribution.map(d => parseInt(String(d.session_count)));

  messagesChart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Sessions',
        data,
        backgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: '#667eea',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: { display: false }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(0, 0, 0, 0.05)' },
          ticks: { font: { size: 12 } }
        },
        x: {
          grid: { display: false },
          ticks: { font: { size: 11 } }
        }
      }
    }
  });
}

export function renderDurationTimelineChart(timeline: Array<{ date: string; avg_duration: number; session_count: number }>): void {
  const ctx = document.getElementById('durationTimelineChart') as HTMLCanvasElement;
  if (!ctx) return;

  if (durationTimelineChart) durationTimelineChart.destroy();

  const labels = timeline.map(d => new Date(d.date).toLocaleDateString('de-AT'));
  const durations = timeline.map(d => d.avg_duration);
  const counts = timeline.map(d => d.session_count);

  durationTimelineChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Avg Duration (s)',
          data: durations,
          borderColor: '#667eea',
          backgroundColor: colors.gradients.purple,
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Session Count',
          data: counts,
          borderColor: '#f093fb',
          backgroundColor: colors.gradients.pink,
          fill: true,
          tension: 0.4,
          yAxisID: 'y1'
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          labels: { padding: 20, usePointStyle: true }
        }
      },
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: { display: true, text: 'Duration (s)' }
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          title: { display: true, text: 'Sessions' },
          grid: { drawOnChartArea: false }
        }
      }
    }
  });
}

export function renderProductRadarChart(data: any): void {
  const ctx = document.getElementById('productRadarChart') as HTMLCanvasElement;
  if (!ctx) return;

  if (productRadarChart) productRadarChart.destroy();

  if (!data || !data.labels || (!data.inStock && !data.outOfStock)) {
    ctx.parentElement!.innerHTML = '<div style="text-align: center; color: #999; padding: 40px;">No product data available</div>';
    return;
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const datasets = [
    {
      label: 'In Stock',
      data: data.inStock || new Array(12).fill(0),
      borderColor: colors.primary[0],
      backgroundColor: `${colors.primary[0]}33`,
      fill: true
    },
    {
      label: 'Out of Stock',
      data: data.outOfStock || new Array(12).fill(0),
      borderColor: colors.primary[1],
      backgroundColor: `${colors.primary[1]}33`,
      fill: true
    }
  ];

  productRadarChart = new Chart(ctx, {
    type: 'radar',
    data: { labels: months, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: 'right',
          labels: { padding: 10, font: { size: 11 } }
        }
      },
      scales: {
        r: {
          beginAtZero: true,
          ticks: { stepSize: 1 }
        }
      }
    }
  });
}

export function renderProductBubbleChart(bubbles: any[]): void {
  const ctx = document.getElementById('productBubbleChart') as HTMLCanvasElement;
  if (!ctx) return;

  if (productBubbleChart) productBubbleChart.destroy();

  if (!bubbles || bubbles.length === 0) {
    ctx.parentElement!.innerHTML = '<div style="text-align: center; color: #999; padding: 40px;">No bubble data available</div>';
    return;
  }

  const monthLabels = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const datasets = bubbles.slice(0, 10).map((bubble, index) => {
    const mentions = bubble.mentions || bubble.r || 1;
    return {
      label: bubble.label || bubble.product_name || 'Unknown',
      data: [{
        x: bubble.x || bubble.month || new Date().getMonth() + 1,
        y: mentions,
        r: Math.min(Math.max(mentions * 2, 8), 40),
        label: bubble.label || bubble.product_name || 'Unknown',
        mentions: mentions
      }],
      backgroundColor: `${colors.primary[index % colors.primary.length]}99`
    };
  });

  productBubbleChart = new Chart(ctx, {
    type: 'bubble',
    data: { datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'right', labels: { font: { size: 11 } } },
        tooltip: {
          callbacks: {
            title: (context: any) => context[0].raw.label,
            label: (context: any) => `${context.raw.mentions} mentions`
          }
        }
      },
      scales: {
        x: {
          title: { display: true, text: 'Month' },
          min: 1,
          max: 12,
          ticks: {
            stepSize: 1,
            callback: function(value: number) {
              return monthLabels[value] || '';
            }
          }
        },
        y: {
          title: { display: true, text: 'Mentions' },
          beginAtZero: true,
          ticks: { stepSize: 1 }
        }
      }
    }
  });
}

export function renderTrafficSourcesChart(sources: { labels: string[]; values: number[] }): void {
  const ctx = document.getElementById('trafficSourcesChart') as HTMLCanvasElement;
  if (!ctx) return;

  if (trafficSourcesChart) trafficSourcesChart.destroy();

  if (!sources || !sources.labels) return;

  trafficSourcesChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: sources.labels,
      datasets: [{
        data: sources.values,
        backgroundColor: colors.primary,
        borderWidth: 3,
        borderColor: '#fff',
        hoverOffset: 15
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      cutout: '60%',
      plugins: {
        legend: {
          position: 'right',
          labels: { padding: 15, font: { size: 13, weight: '500' }, usePointStyle: true }
        }
      }
    }
  });
}

export function renderDeviceTypeChart(devices: { labels: string[]; values: number[] }): void {
  const ctx = document.getElementById('deviceTypeChart') as HTMLCanvasElement;
  if (!ctx) return;

  if (deviceTypeChart) deviceTypeChart.destroy();

  const data = devices || { labels: ['Desktop', 'Mobile', 'Tablet'], values: [0, 0, 0] };

  deviceTypeChart = new Chart(ctx, {
    type: 'pie',
    data: {
      labels: data.labels,
      datasets: [{
        data: data.values,
        backgroundColor: colors.primary.slice(0, data.labels.length),
        borderWidth: 3,
        borderColor: '#fff',
        hoverOffset: 10
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: { position: 'bottom', labels: { padding: 15, font: { size: 13, weight: '500' } } },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
              const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
              return `${context.label}: ${formatNumber(context.parsed)} (${percentage}%)`;
            }
          }
        }
      }
    }
  });
}

export function renderAssistedComparisonChart(comparison: any): void {
  const ctx = document.getElementById('assistedComparisonChart') as HTMLCanvasElement;
  if (!ctx) return;

  if (assistedComparisonChart) assistedComparisonChart.destroy();

  const chatbot = comparison.chatbot || {};
  const organic = comparison.organic || {};

  assistedComparisonChart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: ['Sessions', 'Conversions', 'Conversion Rate (%)', 'Revenue (€)'],
      datasets: [
        {
          label: '💬 Chatbot Users',
          data: [chatbot.sessions || 0, chatbot.conversions || 0, chatbot.cvr || 0, chatbot.revenue || 0],
          backgroundColor: '#667eea'
        },
        {
          label: '🌐 Organic Users',
          data: [organic.sessions || 0, organic.conversions || 0, organic.cvr || 0, organic.revenue || 0],
          backgroundColor: '#f093fb'
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        title: { display: true, text: 'Performance: Chatbot vs Organic Users' },
        legend: { position: 'top' }
      },
      scales: {
        y: { beginAtZero: true }
      }
    }
  });
}

export function renderFunnelChart(funnel: { stages: string[]; counts: number[] }): void {
  const container = document.getElementById('funnelContainer');
  if (!container) return;

  if (!funnel || !funnel.stages || !funnel.counts) {
    container.innerHTML = '<div class="funnel-empty">No funnel data available</div>';
    return;
  }

  const maxCount = Math.max(...funnel.counts);
  const minWidth = 120;
  const maxWidth = 400;

  let html = '';

  funnel.stages.forEach((stage, i) => {
    const count = funnel.counts[i];
    const prevCount = i > 0 ? funnel.counts[i - 1] : count;
    const dropoffRate = prevCount > 0 ? ((prevCount - count) / prevCount * 100).toFixed(1) : 0;
    const conversionRate = funnel.counts[0] > 0 ? ((count / funnel.counts[0]) * 100).toFixed(1) : 0;
    const widthRatio = maxCount > 0 ? (count / maxCount) : 0.5;
    const width = Math.max(minWidth, minWidth + (maxWidth - minWidth) * Math.sqrt(widthRatio));
    const color = colors.funnel[i % colors.funnel.length];

    html += `
      <div class="funnel-stage" style="width: 100%;">
        ${i > 0 ? `<div class="funnel-rate">${conversionRate}%</div>` : ''}
        <div class="funnel-bar" style="width: ${width}px; background: ${color};">
          <div class="funnel-content">
            <span class="funnel-label">${stage}</span>
            <span class="funnel-count">${formatNumber(count)}</span>
          </div>
        </div>
        ${i > 0 && Number(dropoffRate) > 0 ? `<div class="funnel-dropoff">-${dropoffRate}%</div>` : ''}
      </div>
      ${i < funnel.stages.length - 1 ? '<div class="funnel-arrow">▼</div>' : ''}
    `;
  });

  const overallConversion = funnel.counts[0] > 0
    ? ((funnel.counts[funnel.counts.length - 1] / funnel.counts[0]) * 100).toFixed(1)
    : 0;

  html += `
    <div style="margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px; text-align: center; width: 100%;">
      <div style="font-size: 14px; color: #7f8c8d; margin-bottom: 5px;">Overall Conversion Rate</div>
      <div style="font-size: 28px; font-weight: 700; color: ${parseFloat(String(overallConversion)) > 5 ? '#27ae60' : parseFloat(String(overallConversion)) > 1 ? '#f39c12' : '#e74c3c'};">
        ${overallConversion}%
      </div>
      <div style="font-size: 12px; color: #95a5a6; margin-top: 5px;">
        ${formatNumber(funnel.counts[0])} sessions → ${formatNumber(funnel.counts[funnel.counts.length - 1])} purchases
      </div>
    </div>
  `;

  container.innerHTML = html;
}

// Cleanup all charts
export function destroyAllCharts(): void {
  if (durationChart) durationChart.destroy();
  if (messagesChart) messagesChart.destroy();
  if (durationTimelineChart) durationTimelineChart.destroy();
  if (productRadarChart) productRadarChart.destroy();
  if (productBubbleChart) productBubbleChart.destroy();
  if (trafficSourcesChart) trafficSourcesChart.destroy();
  if (deviceTypeChart) deviceTypeChart.destroy();
  if (assistedComparisonChart) assistedComparisonChart.destroy();
}
